<template lang="pug">
md-layout(md-column='', md-align='start')
  // Page loader
  md-progress.md-accent(md-indeterminate='', v-if='loadingComponent')
  // Page content
  md-theme(md-name='blue', v-else='')
    // Title
    breadcrumbs#scrollToError(section='Domain Lists', :nav='nav')

    // Form
    md-layout.form-wrapper(:md-gutter='16')
      md-layout(md-flex='100', md-flex-small='100')
        md-whiteframe.form-wrapper-frame(md-elevation='3')
          metadata-form(title='Domain List', :entity='domainList', :form-submitted='formSubmitted', :validations='$v.domainList')
          status-radio(v-model='domainList.status')
          .md-headline#targets Targets
          md-chips#domain-input.domainContainer(
            v-model='domainList.domains',
            :class='{"md-input-invalid": formSubmitted && $v.domainList.domains.$invalid}',
            md-input-placeholder='Type to add a new domain...',
            @change='onDomainsChanged'
          )
          md-layout(md-flex='40 ').bulkUpload
            md-input-container
              label {{bulkStatus}}
              md-file.search-btn(v-model='fileName', accept='.csv', @selected='onFileSelected', :disabled='loadingComponent')
              md-button.search-btn.md-icon-button(@click='takeDomains()', :disabled='loadingComponent || this.file === null' )
                md-icon.md-icon(md-iconset="fa fa-upload")
          div.btn-del-div
            md-button#btn-delete(@click='deleteDomainList') Delete all domains

      md-layout.errorMsg(md-align='center')
        span(v-if='formSubmitted && hasDomains && $v.domainList.domains.$each.$invalid') Some of the domains has a wrong format.
        span(v-if='formSubmitted && !$v.domainList.domains.required') You must enter at least one domain.
        span {{ errorStatus }}
        stitch-loader(:loading='creatingOrUpdating', :size='40')
        md-button#submit-btn.md-raised.md-primary(@click='onFormSubmit', v-show='!creatingOrUpdating', :disabled="$v.$pending")
          | {{ editMode ? 'Update' : 'Create' }}
</template>

<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import { isValidDomain } from "../../services/validatorService";
import StitchLoader from "../../shared-components/Loader";
import domainService from "../../services/domainService";
import MetadataForm from "../../shared-components/forms/MetadataForm";
import StatusRadio from "../../shared-components/StatusRadio";
import Breadcrumbs from "../../shared-components/Breadcrumbs";

export default {
  mixins: [validationMixin],
  components: {
    StitchLoader,
    MetadataForm,
    StatusRadio,
    Breadcrumbs,
  },
  computed: {
    nav() {
      return [
        { title: "Domain Lists", route: "DomainList" },
        { title: this.editMode ? "Edit Domain List" : "New Domain List" },
      ];
    },
    editMode() {
      return !!this.$route.params.id;
    },
    hasDomains() {
      return this.domainList.domains.length > 0;
    },
  },
  data() {
    return {
      domainList: {
        name: "",
        description: "",
        tags: [],
        status: "draft",
        domains: [],
      },
      loadingComponent: false,
      creatingOrUpdating: false,
      domainListOriginalName: null,
      formSubmitted: false,
      errorStatus: "",
      bulkStatus: "Add domains by CSV",
      fileName: "",
      file: null,
    };
  },
  validations: {
    domainList: {
      name: {
        required,
        async isUnique(value) {
          const hasToCheck =
            !this.editMode || value !== this.domainListOriginalName;
          if (value && hasToCheck) {
            const response = await domainService.getList({
              exactName: value,
              limit: 1,
            });
            return response.data.total === 0;
          } else {
            return true;
          }
        },
      },
      shortDescription: {
        default: true
      },
      description: {
        default: true
      },
      domains: {
        required,
        $each: {
          isValidDomain(value) {
            return isValidDomain(value);
          },
        },
      },
    },
  },
  methods: {
    showAlertToast(text, icon){
        this.$swal({
              text: text,
              position: 'bottom-left',
              icon: icon,
              toast: true,
              timer: 3000,
              timerProgressBar: true,
              showConfirmButton: false,
              }
            )
      },

    onDomainsChanged(domains) {
      // Workaround to allow pasting multiple domains at the same time.
      if (domains.length > 0) {
        const lastIndex = domains.length - 1;
        let domain = domains[lastIndex];
        domain = domain.replace(/[\n\r]/g, " ").replace(/\s\s+/g, " ");
        const domainList = domain.split(" ");
        if (domainList.length > 1) {
          this.domainList.domains.splice(lastIndex, 1, ...domainList);
        }
      }
    },
    createDomainList() {
      this.creatingOrUpdating = true;
      domainService
        .create(this.domainList)
        .then(() => {
          this.showAlertToast('Domain list created successfully', 'success');
          this.$delayedNavigation(this.$router, 'DomainList');
        })
        .catch(() => {
          this.showAlertToast('Domain list could not be created. Please, try again later.', 'error');
        })
        .then(() => {
          this.creatingOrUpdating = false;
        });
    },

    updateDomainList() {
      this.creatingOrUpdating = true;
      const domainList = Object.assign(this.domainList, {
        _id: this.$route.params.id,
      });
      domainService
        .update(this.$route.params.id, domainList)
        .then(() => {
          this.showAlertToast('Domain list updated successfully', 'success');
          this.$delayedNavigation(this.$router, 'DomainList');
        })
        .catch(() => {
          this.showAlertToast('Domain list could not be updated. Please, try again later.', 'error');
        })
        .then(() => {
          this.creatingOrUpdating = false;
        });
    },

    deleteDomainList() {
      this.domainList.domains = [];
    },

    checkDuplicateOrBlacklistedDomain() {
      if (!this.$v.$invalid) {
        this.creatingOrUpdating = true;
        let blackListDomain = false;
        let blackListDomainNames = [];
        domainService
          .getBlackList()
          .then((blackListedDomains) => {
            const domainsInBlackList = Object.values(blackListedDomains.data);

            const newDomainNamesInCSV = JSON.parse(
              JSON.stringify(this.domainList.domains)
            );

            newDomainNamesInCSV.some((element) => {
              if (domainsInBlackList.includes(element)) {
                blackListDomain = true;
                if (!blackListDomainNames.includes(element)) {
                  blackListDomainNames.push(element);
                }
              }
            });

            if (blackListDomain) {
              if (blackListDomainNames.length > 3) {
                const moreBlacklistedNum = blackListDomainNames.length - 3;
                this.errorStatus = `You already have blacklisted domain(s): ${blackListDomainNames[0]}, ${blackListDomainNames[1]}, ${blackListDomainNames[2]} and ${moreBlacklistedNum} more`;
              } else {
                this.errorStatus = `You already have blacklisted domain(s): ${blackListDomainNames.toString()}`;
              }
              this.creatingOrUpdating = false;
              return;
            } else {
              if (this.editMode) {
                this.updateDomainList();
              } else {
                this.createDomainList();
              }
              return;
            }
          })
          .catch((error) => {
            console.error(
              "Domain list could not be created. Please, try again later.",
              error
            );
          });
      }
    },
    onFormSubmit() {
      if(this.$v.$invalid) {
        const scrollToError = this.$el.querySelector("#scrollToError");
        scrollToError.scrollIntoView({ behavior: "smooth" })
        this.formSubmitted = true;
      } else{
        this.checkDuplicateOrBlacklistedDomain();
      }
    },
    onFileSelected(files) {
      this.bulkStatus = "Add domains by CSV";
      this.file = files[0];
    },
    takeDomains() {
      this.bulkStatus = "Adding domains by CSV file, please wait...";
      const reader = new FileReader();
      reader.readAsText(this.file);
      reader.onload = (event) => {
        const csvParsedData = event.target.result;
        if (!csvParsedData) {
          this.bulkStatus = "Error uploading";
          this.showAlertToast('Domain list could not be updated. Malformed CSV', 'error');
          return;
        }
        const CSVfilteredDuplicatedData = csvParsedData.split(/\r?\n/).filter( // remove duplicated value items and empty values
          (item, index) => csvParsedData.split(/\r?\n/).indexOf(item) === index && !!item
        );
        this.bulkStatus = "Completed!";
        this.domainList.domains = JSON.parse(
          JSON.stringify(this.domainList.domains)
        ).concat(CSVfilteredDuplicatedData);
        setTimeout(() => {
          this.bulkStatus = "Upload domains by CSV";
        }, 1500);
      };
    },
  },
  mounted() {
    if (this.editMode) {
      this.loadingComponent = true;
      domainService
        .get(this.$route.params.id, { use_mongoose: 1 })
        .then((response) => {
          this.domainList = response.data.domainList;
          this.domainListOriginalName = this.domainList.name;
        })
        .catch(() => {
          this.showAlertToast('Domain list data could not be retrieved. Please, try again later', 'error');
        })
        .then(() => {
          this.loadingComponent = false;
        });
    }
  },
};
</script>

<style lang="scss" scoped>
.form-wrapper {
  width: 100%;
  padding: 25px 20%;

  @media (max-width: 1200px) {
    padding: 10px 0.5%;
    margin-left: 0;
  }

  .md-layout {
    margin-bottom: 5px;
  }
}

.form-wrapper-frame {
  width: 100%;
  padding: 25px;
  margin-bottom: 15px;
}

.title-icon {
  margin-top: -5px;
}

#submit-btn {
  margin: 25px 0;
  width: 33%;
  padding: 6px;
  font-size: 1.1em;
}

#targets {
  margin-top: 15px;
}

.validation-error-message {
  width: 100%;
  margin-top: 10px;
  padding: 15px;
  background-color: #ff5722;
  color: #ebfaff;
  font-size: 1.1em;

  .md-icon {
    margin-top: -2px;
    margin-right: 5px;
  }
}

.bulkUpload {
  margin: auto; 
  width: 40%;
}

.domainContainer{
  max-height: 400px;
  overflow-y: auto;
}

.btn-del-div {
  display: flex;
}

#btn-delete {
  background: hwb(344 12% 26% / 0.603);
  color: #fff;
  margin: auto;
  padding: 2px 15px;
  border-radius: 4px;
  font-size: 1.1em;
}

#btn-delete:hover {
  color: #fff !important;
  background-color: #bc1f4a !important;
  transform: scale(1.02);
}

.errorMsg {
  color: #ff5722;
  font-size: 16px;
}
</style>



// WEBPACK FOOTER //
// src/spa/domain/DomainForm.vue